import type { PlanTier } from '../schemas/entitlements/plan-tier.schema'
import { themeKeySchema, type ThemeKey } from '../schemas/entitlements/theme-key.schema'
import { FeatureKeys, type FeatureKey } from '../schemas/entitlements/feature-key.schema'
import { Currency, type CurrencyValue } from '../schemas/common/currencies-schemas'

// Everything a Basic brand gets. Premium and Pro inherit all of it.
export const BASIC_FEATURES: FeatureKey[] = [
  FeatureKeys.Regions,
  FeatureKeys.LocationSettings,
  FeatureKeys.RoomManagement,
  FeatureKeys.CapacityManagement,
  FeatureKeys.BrandCustomisation,
  FeatureKeys.WebsiteEmbedWidgets,
  FeatureKeys.MembershipsPackages,
  FeatureKeys.ServiceLifecycle,
  FeatureKeys.CreditManagement,
  FeatureKeys.ServiceAddOns,
  FeatureKeys.PlanChange,
  FeatureKeys.SchedulingClasses,
  FeatureKeys.InstructorAssignment,
  FeatureKeys.Bookings,
  FeatureKeys.BookingRules,
  FeatureKeys.Waitlist,
  FeatureKeys.Checkin,
  FeatureKeys.BarcodeCheckin,
  FeatureKeys.AttendanceTracking,
  FeatureKeys.SessionFeedback,
  FeatureKeys.ClientManagement,
  FeatureKeys.ClientCustomProperties,
  FeatureKeys.ClientFiles,
  FeatureKeys.ClientAgreements,
  FeatureKeys.CustomForms,
  FeatureKeys.ClientImport,
  FeatureKeys.SupportChat,
  FeatureKeys.Invoicing,
  FeatureKeys.BillingPayments,
  FeatureKeys.PaymentNotifications,
  FeatureKeys.FailedPaymentRecovery,
  FeatureKeys.PaymentMethods,
  FeatureKeys.Refunds,
  FeatureKeys.AccountCredits,
  FeatureKeys.Disputes,
  FeatureKeys.SitePayouts,
  FeatureKeys.PaymentGateways,
  FeatureKeys.TaxConfiguration,
  FeatureKeys.Proration,
  FeatureKeys.RetailPos,
  FeatureKeys.TerminalReaders,
  FeatureKeys.PromoCodes,
  FeatureKeys.StandardReports,
  FeatureKeys.StaffManagement,
  FeatureKeys.AccessPolicies,
  FeatureKeys.StaffSmartGroups,
  FeatureKeys.CommunicationTemplates,
  FeatureKeys.SmsMessaging,
  FeatureKeys.PushNotifications,
  FeatureKeys.LeadForms,
  FeatureKeys.SmartContactLists,
  FeatureKeys.SocialMediaSettings,
  FeatureKeys.ContentFaqs,
  FeatureKeys.MemberApp,
  FeatureKeys.MobileBooking,
  FeatureKeys.MobileCheckin,
  FeatureKeys.ClasspassIntegration,
  FeatureKeys.ZapierIntegration,
  FeatureKeys.ZoomIntegration,
  FeatureKeys.WellhubIntegration,
  FeatureKeys.InceptionIntegration,
  FeatureKeys.TopazIntegration,
  FeatureKeys.HapanaAssistant,
  FeatureKeys.SharedInfrastructure,
  FeatureKeys.Rbac,
  FeatureKeys.ProfessionalServices,
]

// Added at Premium. Pro inherits these too.
export const PREMIUM_ONLY_FEATURES: FeatureKey[] = [
  FeatureKeys.MultiLocation,
  FeatureKeys.MultiRoom,
  FeatureKeys.SpotBasedBooking,
  FeatureKeys.SharedCredits,
  FeatureKeys.GuestPasses,
  FeatureKeys.NoShowFee,
  FeatureKeys.ReferAFriend,
  FeatureKeys.BookABuddy,
  FeatureKeys.SmartClientGroups,
  FeatureKeys.AdvanceBooking,
  FeatureKeys.GiftCards,
  FeatureKeys.Payroll,
  FeatureKeys.Commissions,
  FeatureKeys.CampaignTemplates,
  FeatureKeys.Whatsapp,
  FeatureKeys.ContentSuite,
  FeatureKeys.ContentDelivery,
  FeatureKeys.ReadApi,
  FeatureKeys.ApiWebhooksRead,
  FeatureKeys.ApiSandbox,
  FeatureKeys.ApiPremiumActions,
  FeatureKeys.FacebookPixel,
  FeatureKeys.GoogleTagManager,
  FeatureKeys.GrowHqManagement,
  FeatureKeys.AccurofitIntegration,
  FeatureKeys.SpiviIntegration,
  FeatureKeys.Marketplace,
  FeatureKeys.HubspotIntegration,
  // Premium per the Jul–Dec 2026 product roadmap (Family Accounts, October release).
  // The July '26 Notion breakdown lists it under Pro → Client Management; the roadmap
  // is the later source and wins. Confirmed with Lakshmi on OC-320.
  FeatureKeys.FamilyAccounts,
]

// Added at Pro only.
export const PRO_ONLY_FEATURES: FeatureKey[] = [
  // eGym is a Pro-tier integration per the product roadmap — reconciled from Premium in OC-1181 so
  // the enforcement (the eGym sync gate) matches. Distinct from the Premium integration add-ons
  // (Accurofit / Spivi / HubSpot / Marketplace), which stay Premium.
  FeatureKeys.EgymIntegration,
  // BI tooling (Embedded BI Dashboards) is Pro-only — Lakshmi's call on OC-320 (comment 19334cf2),
  // superseding the earlier Premium reading of the catalogue.
  FeatureKeys.BiDashboards,
  // eGym is Pro per the product roadmap (July '26: "eGym — PRO"). It is absent from the Notion
  // plans doc; the roadmap is the tie-breaker for tier calls, so it sits at Pro, not Premium.
  FeatureKeys.EgymIntegration,
  FeatureKeys.HqAccount,
  FeatureKeys.HeadOfficeContent,
  FeatureKeys.SiteGroups,
  FeatureKeys.AdminAccessGroups,
  FeatureKeys.StaffIpRestrictions,
  FeatureKeys.AdminProvisioning,
  FeatureKeys.CrossLocationAnalytics,
  FeatureKeys.ConnectedSharedSites,
  FeatureKeys.TieredPerks,
  FeatureKeys.HolidayFee,
  FeatureKeys.MemberMovement,
  // Placeholder tier: native class covers' plan placement is undecided (PRD §9 Q3, add-on vs plan).
  FeatureKeys.ClassCovers,
  FeatureKeys.CorporatePayouts,
  FeatureKeys.RoyaltyDistribution,
  FeatureKeys.ScheduledReports,
  FeatureKeys.BigqueryReports,
  FeatureKeys.KpiPerformanceReport,
  FeatureKeys.BrandWideTemplates,
  FeatureKeys.CustomBrandedApp,
  FeatureKeys.MemberAppPlanYourVisit,
  FeatureKeys.FullApiReadWrite,
  FeatureKeys.WebhookEventsReadWrite,
  FeatureKeys.ApiSelfServiceKeys,
  FeatureKeys.BrandApiKeys,
  FeatureKeys.LocationApiKeys,
  FeatureKeys.IsolatedDatabase,
  FeatureKeys.FullAuditLogs,
]

export const THEME_FEATURES: Record<ThemeKey, ReadonlySet<FeatureKey>> = {
  access: new Set([
    FeatureKeys.AccessMacDoor,
    FeatureKeys.AccessScheduling,
    FeatureKeys.AccessEventLogging,
    FeatureKeys.AccessReporting,
    FeatureKeys.AccessSiteDoorManagement,
    FeatureKeys.AccessDigitalExperience,
    FeatureKeys.AccessController,
  ]),
  experience: new Set([
    FeatureKeys.ExperiencePerformance,
    FeatureKeys.ExperienceChallenges,
    FeatureKeys.ExperienceLeaderboards,
    FeatureKeys.ExperienceStreaks,
    FeatureKeys.ExperienceBadges,
    FeatureKeys.ExperienceMilestones,
  ]),
  retention: new Set([
    FeatureKeys.RetentionDashboards,
    FeatureKeys.RetentionAtRisk,
    FeatureKeys.RetentionChurnAnalysis,
    FeatureKeys.RetentionEngagement,
    FeatureKeys.RetentionWinback,
    FeatureKeys.RetentionAttendanceTrends,
  ]),
  hapana_ai: new Set([
    FeatureKeys.AiActions,
    FeatureKeys.AiDataInsights,
    FeatureKeys.AiPredictive,
    FeatureKeys.AiRecommendations,
    FeatureKeys.AiCharts,
    FeatureKeys.AiMetering,
    FeatureKeys.AiNaturalLanguageAnalytics,
  ]),
}

export const PLAN_FEATURES: Record<PlanTier, ReadonlySet<FeatureKey>> = {
  basic: new Set(BASIC_FEATURES),
  premium: new Set([...BASIC_FEATURES, ...PREMIUM_ONLY_FEATURES]),
  pro: new Set([...BASIC_FEATURES, ...PREMIUM_ONLY_FEATURES, ...PRO_ONLY_FEATURES]),
}

// Minimum plan tier required to purchase each theme, per the OC-320 "Themes &
// Minimum Tier Required" table. meetsMinimumPlan is cumulative, so a minimum of
// 'basic' means basic, premium and pro can all buy that theme — which is how a
// theme is "available on one or more tiers". Themes are auto-activated at runtime
// per TIER_THEMES; a future phase lets customers activate themes themselves.
export const THEME_ELIGIBILITY: Record<ThemeKey, PlanTier> = {
  access: 'basic',
  experience: 'premium',
  retention: 'premium',
  hapana_ai: 'pro',
}

// Themes auto-activated by plan tier (OC-320). basic → access; premium & pro → access + experience.
// retention and hapana_ai are intentionally not activated this phase — they have no Core surface yet.
// A future phase lets customers activate themes themselves; that becomes an additive union on top.
// OC-852: every theme is now a purchasable ADD-ON toggled per brand in admin — none is auto-active by
// tier. A tier grants only its PLAN_FEATURES; theme features are entitled solely when the brand has an
// active CorporateThemeSubscription (see EntitlementService.resolveActiveThemes). THEME_ELIGIBILITY
// still gates WHICH themes a tier may purchase. NOTE: making this live for existing brands needs a
// backfill that subscribes them to the themes their tier previously auto-included.
export const TIER_THEMES: Record<PlanTier, ReadonlySet<ThemeKey>> = {
  basic: new Set(),
  premium: new Set(),
  pro: new Set(),
}

export function getThemesForTier(tier: PlanTier): ThemeKey[] {
  return [...TIER_THEMES[tier]]
}

// The add-ons a tier may PURCHASE (THEME_ELIGIBILITY), as distinct from getThemesForTier — the
// ones a tier auto-activates, which is none today. This is what plan dialogs list and count.
export function getEligibleThemesForTier(tier: PlanTier): ThemeKey[] {
  return themeKeySchema.options.filter((theme) => meetsMinimumPlan(tier, THEME_ELIGIBILITY[theme]))
}

export function getAvailableThemes(): ThemeKey[] {
  // Every theme is a purchasable add-on now (nothing auto-active per TIER_THEMES), so a theme is
  // "available" once it has features wired. The add-on UI applies per-tier eligibility (THEME_ELIGIBILITY)
  // and RELEASED_THEMES (a not-yet-released theme is shown DISABLED rather than hidden).
  return themeKeySchema.options.filter((theme) => THEME_FEATURES[theme].size > 0)
}

// Themes released to sell. Access and Experience are finished; Retention and Hapana AI have their
// features wired but no finished surface yet, so the add-on UI shows them DISABLED ("coming soon")
// until they ship. This gates the UI only — it does not change eligibility or the entitlement engine.
export const RELEASED_THEMES: ReadonlySet<ThemeKey> = new Set<ThemeKey>([
  themeKeySchema.enum.access,
  themeKeySchema.enum.experience,
])

export function isThemeReleased(theme: ThemeKey): boolean {
  return RELEASED_THEMES.has(theme)
}

// All features a tier is entitled to, including the features of its auto-activated themes. The
// engine and the tier tests both derive from this so a themes-included "what's granted" set cannot
// drift from what the entitlement engine actually returns.
export function getTierEntitledFeatures(tier: PlanTier): Set<FeatureKey> {
  const features = new Set<FeatureKey>(PLAN_FEATURES[tier])
  for (const theme of getThemesForTier(tier)) {
    for (const feature of THEME_FEATURES[theme]) {
      features.add(feature)
    }
  }
  return features
}

export const PLAN_HIERARCHY: Record<PlanTier, number> = {
  basic: 0,
  premium: 1,
  pro: 2,
}

export function meetsMinimumPlan(currentPlan: PlanTier, requiredPlan: PlanTier): boolean {
  return PLAN_HIERARCHY[currentPlan] >= PLAN_HIERARCHY[requiredPlan]
}

export function getRequiredPlanForFeature(featureKey: FeatureKey): PlanTier | null {
  if (PLAN_FEATURES.basic.has(featureKey)) {
    return 'basic'
  }
  if (PLAN_FEATURES.premium.has(featureKey)) {
    return 'premium'
  }
  if (PLAN_FEATURES.pro.has(featureKey)) {
    return 'pro'
  }
  return null
}

export function getThemeForFeature(featureKey: FeatureKey): ThemeKey | null {
  // Iterate the schema's own options rather than Object.entries, which widens the
  // key to `string` and would force a cast back to ThemeKey.
  for (const themeKey of themeKeySchema.options) {
    if (THEME_FEATURES[themeKey].has(featureKey)) {
      return themeKey
    }
  }
  return null
}

export const FEATURE_LABEL_KEYS: Record<FeatureKey, string> = {
  [FeatureKeys.Regions]: 'entitlements.features.regions',
  [FeatureKeys.LocationSettings]: 'entitlements.features.locationSettings',
  [FeatureKeys.RoomManagement]: 'entitlements.features.roomManagement',
  [FeatureKeys.CapacityManagement]: 'entitlements.features.capacityManagement',
  [FeatureKeys.BrandCustomisation]: 'entitlements.features.brandCustomisation',
  [FeatureKeys.WebsiteEmbedWidgets]: 'entitlements.features.websiteEmbedWidgets',
  [FeatureKeys.MembershipsPackages]: 'entitlements.features.membershipsPackages',
  [FeatureKeys.ServiceLifecycle]: 'entitlements.features.serviceLifecycle',
  [FeatureKeys.CreditManagement]: 'entitlements.features.creditManagement',
  [FeatureKeys.ServiceAddOns]: 'entitlements.features.serviceAddOns',
  [FeatureKeys.PlanChange]: 'entitlements.features.planChange',
  [FeatureKeys.SchedulingClasses]: 'entitlements.features.schedulingClasses',
  [FeatureKeys.InstructorAssignment]: 'entitlements.features.instructorAssignment',
  [FeatureKeys.ClassCovers]: 'entitlements.features.classCovers',
  [FeatureKeys.Bookings]: 'entitlements.features.bookings',
  [FeatureKeys.BookingRules]: 'entitlements.features.bookingRules',
  [FeatureKeys.Waitlist]: 'entitlements.features.waitlist',
  [FeatureKeys.Checkin]: 'entitlements.features.checkin',
  [FeatureKeys.BarcodeCheckin]: 'entitlements.features.barcodeCheckin',
  [FeatureKeys.AttendanceTracking]: 'entitlements.features.attendanceTracking',
  [FeatureKeys.SessionFeedback]: 'entitlements.features.sessionFeedback',
  [FeatureKeys.ClientManagement]: 'entitlements.features.clientManagement',
  [FeatureKeys.ClientCustomProperties]: 'entitlements.features.clientCustomProperties',
  [FeatureKeys.ClientFiles]: 'entitlements.features.clientFiles',
  [FeatureKeys.ClientAgreements]: 'entitlements.features.clientAgreements',
  [FeatureKeys.CustomForms]: 'entitlements.features.customForms',
  [FeatureKeys.ClientImport]: 'entitlements.features.clientImport',
  [FeatureKeys.SupportChat]: 'entitlements.features.supportChat',
  [FeatureKeys.Invoicing]: 'entitlements.features.invoicing',
  [FeatureKeys.BillingPayments]: 'entitlements.features.billingPayments',
  [FeatureKeys.PaymentNotifications]: 'entitlements.features.paymentNotifications',
  [FeatureKeys.FailedPaymentRecovery]: 'entitlements.features.failedPaymentRecovery',
  [FeatureKeys.PaymentMethods]: 'entitlements.features.paymentMethods',
  [FeatureKeys.Refunds]: 'entitlements.features.refunds',
  [FeatureKeys.AccountCredits]: 'entitlements.features.accountCredits',
  [FeatureKeys.Disputes]: 'entitlements.features.disputes',
  [FeatureKeys.SitePayouts]: 'entitlements.features.sitePayouts',
  [FeatureKeys.PaymentGateways]: 'entitlements.features.paymentGateways',
  [FeatureKeys.TaxConfiguration]: 'entitlements.features.taxConfiguration',
  [FeatureKeys.Proration]: 'entitlements.features.proration',
  [FeatureKeys.RetailPos]: 'entitlements.features.retailPos',
  [FeatureKeys.TerminalReaders]: 'entitlements.features.terminalReaders',
  [FeatureKeys.PromoCodes]: 'entitlements.features.promoCodes',
  [FeatureKeys.StandardReports]: 'entitlements.features.standardReports',
  [FeatureKeys.StaffManagement]: 'entitlements.features.staffManagement',
  [FeatureKeys.AccessPolicies]: 'entitlements.features.accessPolicies',
  [FeatureKeys.StaffSmartGroups]: 'entitlements.features.staffSmartGroups',
  [FeatureKeys.CommunicationTemplates]: 'entitlements.features.communicationTemplates',
  [FeatureKeys.SmsMessaging]: 'entitlements.features.smsMessaging',
  [FeatureKeys.PushNotifications]: 'entitlements.features.pushNotifications',
  [FeatureKeys.LeadForms]: 'entitlements.features.leadForms',
  [FeatureKeys.SmartContactLists]: 'entitlements.features.smartContactLists',
  [FeatureKeys.SocialMediaSettings]: 'entitlements.features.socialMediaSettings',
  [FeatureKeys.ContentFaqs]: 'entitlements.features.contentFaqs',
  [FeatureKeys.MemberApp]: 'entitlements.features.memberApp',
  [FeatureKeys.MobileBooking]: 'entitlements.features.mobileBooking',
  [FeatureKeys.MobileCheckin]: 'entitlements.features.mobileCheckin',
  [FeatureKeys.ClasspassIntegration]: 'entitlements.features.classpassIntegration',
  [FeatureKeys.ZapierIntegration]: 'entitlements.features.zapierIntegration',
  [FeatureKeys.ZoomIntegration]: 'entitlements.features.zoomIntegration',
  [FeatureKeys.WellhubIntegration]: 'entitlements.features.wellhubIntegration',
  [FeatureKeys.InceptionIntegration]: 'entitlements.features.inceptionIntegration',
  [FeatureKeys.TopazIntegration]: 'entitlements.features.topazIntegration',
  [FeatureKeys.HapanaAssistant]: 'entitlements.features.hapanaAssistant',
  [FeatureKeys.SharedInfrastructure]: 'entitlements.features.sharedInfrastructure',
  [FeatureKeys.Rbac]: 'entitlements.features.rbac',
  [FeatureKeys.ProfessionalServices]: 'entitlements.features.professionalServices',
  [FeatureKeys.MultiLocation]: 'entitlements.features.multiLocation',
  [FeatureKeys.MultiRoom]: 'entitlements.features.multiRoom',
  [FeatureKeys.SpotBasedBooking]: 'entitlements.features.spotBasedBooking',
  [FeatureKeys.SharedCredits]: 'entitlements.features.sharedCredits',
  [FeatureKeys.GuestPasses]: 'entitlements.features.guestPasses',
  [FeatureKeys.ReferAFriend]: 'entitlements.features.referAFriend',
  [FeatureKeys.BookABuddy]: 'entitlements.features.bookABuddy',
  [FeatureKeys.SmartClientGroups]: 'entitlements.features.smartClientGroups',
  [FeatureKeys.AdvanceBooking]: 'entitlements.features.advanceBooking',
  [FeatureKeys.GiftCards]: 'entitlements.features.giftCards',
  [FeatureKeys.BiDashboards]: 'entitlements.features.biDashboards',
  [FeatureKeys.Payroll]: 'entitlements.features.payroll',
  [FeatureKeys.Commissions]: 'entitlements.features.commissions',
  [FeatureKeys.CampaignTemplates]: 'entitlements.features.campaignTemplates',
  [FeatureKeys.Whatsapp]: 'entitlements.features.whatsapp',
  [FeatureKeys.ContentSuite]: 'entitlements.features.contentSuite',
  [FeatureKeys.ContentDelivery]: 'entitlements.features.contentDelivery',
  [FeatureKeys.ReadApi]: 'entitlements.features.readApi',
  [FeatureKeys.ApiWebhooksRead]: 'entitlements.features.apiWebhooksRead',
  [FeatureKeys.ApiSandbox]: 'entitlements.features.apiSandbox',
  [FeatureKeys.ApiPremiumActions]: 'entitlements.features.apiPremiumActions',
  [FeatureKeys.FacebookPixel]: 'entitlements.features.facebookPixel',
  [FeatureKeys.GoogleTagManager]: 'entitlements.features.googleTagManager',
  [FeatureKeys.GrowHqManagement]: 'entitlements.features.growHqManagement',
  [FeatureKeys.AccurofitIntegration]: 'entitlements.features.accurofitIntegration',
  [FeatureKeys.SpiviIntegration]: 'entitlements.features.spiviIntegration',
  [FeatureKeys.Marketplace]: 'entitlements.features.marketplace',
  [FeatureKeys.HubspotIntegration]: 'entitlements.features.hubspotIntegration',
  [FeatureKeys.EgymIntegration]: 'entitlements.features.egymIntegration',
  [FeatureKeys.HqAccount]: 'entitlements.features.hqAccount',
  [FeatureKeys.HeadOfficeContent]: 'entitlements.features.headOfficeContent',
  [FeatureKeys.SiteGroups]: 'entitlements.features.siteGroups',
  [FeatureKeys.AdminAccessGroups]: 'entitlements.features.adminAccessGroups',
  [FeatureKeys.StaffIpRestrictions]: 'entitlements.features.staffIpRestrictions',
  [FeatureKeys.AdminProvisioning]: 'entitlements.features.adminProvisioning',
  [FeatureKeys.CrossLocationAnalytics]: 'entitlements.features.crossLocationAnalytics',
  [FeatureKeys.ConnectedSharedSites]: 'entitlements.features.connectedSharedSites',
  [FeatureKeys.TieredPerks]: 'entitlements.features.tieredPerks',
  [FeatureKeys.HolidayFee]: 'entitlements.features.holidayFee',
  [FeatureKeys.NoShowFee]: 'entitlements.features.noShowFee',
  [FeatureKeys.MemberMovement]: 'entitlements.features.memberMovement',
  [FeatureKeys.FamilyAccounts]: 'entitlements.features.familyAccounts',
  [FeatureKeys.CorporatePayouts]: 'entitlements.features.corporatePayouts',
  [FeatureKeys.RoyaltyDistribution]: 'entitlements.features.royaltyDistribution',
  [FeatureKeys.ScheduledReports]: 'entitlements.features.scheduledReports',
  [FeatureKeys.BigqueryReports]: 'entitlements.features.bigqueryReports',
  [FeatureKeys.KpiPerformanceReport]: 'entitlements.features.kpiPerformanceReport',
  [FeatureKeys.BrandWideTemplates]: 'entitlements.features.brandWideTemplates',
  [FeatureKeys.CustomBrandedApp]: 'entitlements.features.customBrandedApp',
  [FeatureKeys.MemberAppPlanYourVisit]: 'entitlements.features.memberAppPlanYourVisit',
  [FeatureKeys.FullApiReadWrite]: 'entitlements.features.fullApiReadWrite',
  [FeatureKeys.WebhookEventsReadWrite]: 'entitlements.features.webhookEventsReadWrite',
  [FeatureKeys.ApiSelfServiceKeys]: 'entitlements.features.apiSelfServiceKeys',
  [FeatureKeys.BrandApiKeys]: 'entitlements.features.brandApiKeys',
  [FeatureKeys.LocationApiKeys]: 'entitlements.features.locationApiKeys',
  [FeatureKeys.IsolatedDatabase]: 'entitlements.features.isolatedDatabase',
  [FeatureKeys.FullAuditLogs]: 'entitlements.features.fullAuditLogs',
  [FeatureKeys.AccessMacDoor]: 'entitlements.features.accessMacDoor',
  [FeatureKeys.AccessScheduling]: 'entitlements.features.accessScheduling',
  [FeatureKeys.AccessEventLogging]: 'entitlements.features.accessEventLogging',
  [FeatureKeys.AccessReporting]: 'entitlements.features.accessReporting',
  [FeatureKeys.AccessSiteDoorManagement]: 'entitlements.features.accessSiteDoorManagement',
  [FeatureKeys.AccessDigitalExperience]: 'entitlements.features.accessDigitalExperience',
  [FeatureKeys.AccessController]: 'entitlements.features.accessController',
  [FeatureKeys.ExperiencePerformance]: 'entitlements.features.experiencePerformance',
  [FeatureKeys.ExperienceChallenges]: 'entitlements.features.experienceChallenges',
  [FeatureKeys.ExperienceLeaderboards]: 'entitlements.features.experienceLeaderboards',
  [FeatureKeys.ExperienceStreaks]: 'entitlements.features.experienceStreaks',
  [FeatureKeys.ExperienceBadges]: 'entitlements.features.experienceBadges',
  [FeatureKeys.ExperienceMilestones]: 'entitlements.features.experienceMilestones',
  [FeatureKeys.RetentionDashboards]: 'entitlements.features.retentionDashboards',
  [FeatureKeys.RetentionAtRisk]: 'entitlements.features.retentionAtRisk',
  [FeatureKeys.RetentionChurnAnalysis]: 'entitlements.features.retentionChurnAnalysis',
  [FeatureKeys.RetentionEngagement]: 'entitlements.features.retentionEngagement',
  [FeatureKeys.RetentionWinback]: 'entitlements.features.retentionWinback',
  [FeatureKeys.RetentionAttendanceTrends]: 'entitlements.features.retentionAttendanceTrends',
  [FeatureKeys.AiActions]: 'entitlements.features.aiActions',
  [FeatureKeys.AiDataInsights]: 'entitlements.features.aiDataInsights',
  [FeatureKeys.AiPredictive]: 'entitlements.features.aiPredictive',
  [FeatureKeys.AiRecommendations]: 'entitlements.features.aiRecommendations',
  [FeatureKeys.AiCharts]: 'entitlements.features.aiCharts',
  [FeatureKeys.AiMetering]: 'entitlements.features.aiMetering',
  [FeatureKeys.AiNaturalLanguageAnalytics]: 'entitlements.features.aiNaturalLanguageAnalytics',
}

export const PLAN_LABEL_KEYS: Record<PlanTier, string> = {
  basic: 'entitlements.plans.basic',
  premium: 'entitlements.plans.premium',
  pro: 'entitlements.plans.pro',
}

export const THEME_LABEL_KEYS: Record<ThemeKey, string> = {
  access: 'entitlements.themes.access',
  experience: 'entitlements.themes.experience',
  retention: 'entitlements.themes.retention',
  hapana_ai: 'entitlements.themes.hapanaAi',
}

export const FEATURE_CATEGORIES = {
  locationBrandManagement: [
    FeatureKeys.Regions,
    FeatureKeys.LocationSettings,
    FeatureKeys.RoomManagement,
    FeatureKeys.CapacityManagement,
    FeatureKeys.BrandCustomisation,
    FeatureKeys.WebsiteEmbedWidgets,
    FeatureKeys.MultiLocation,
    FeatureKeys.MultiRoom,
    FeatureKeys.SpotBasedBooking,
    FeatureKeys.HqAccount,
    FeatureKeys.HeadOfficeContent,
    FeatureKeys.SiteGroups,
    FeatureKeys.AdminAccessGroups,
    FeatureKeys.StaffIpRestrictions,
    FeatureKeys.AdminProvisioning,
    FeatureKeys.CrossLocationAnalytics,
    FeatureKeys.ConnectedSharedSites,
  ],
  membershipsPackages: [
    FeatureKeys.MembershipsPackages,
    FeatureKeys.ServiceLifecycle,
    FeatureKeys.CreditManagement,
    FeatureKeys.ServiceAddOns,
    FeatureKeys.PlanChange,
    FeatureKeys.SharedCredits,
    FeatureKeys.GuestPasses,
    FeatureKeys.ReferAFriend,
    FeatureKeys.TieredPerks,
    FeatureKeys.HolidayFee,
    FeatureKeys.NoShowFee,
  ],
  schedulingClassManagement: [
    FeatureKeys.SchedulingClasses,
    FeatureKeys.InstructorAssignment,
    FeatureKeys.ClassCovers,
  ],
  bookingsAttendance: [
    FeatureKeys.Bookings,
    FeatureKeys.BookingRules,
    FeatureKeys.Waitlist,
    FeatureKeys.Checkin,
    FeatureKeys.BarcodeCheckin,
    FeatureKeys.AttendanceTracking,
    FeatureKeys.SessionFeedback,
    FeatureKeys.AdvanceBooking,
  ],
  clientManagement: [
    FeatureKeys.ClientManagement,
    FeatureKeys.ClientCustomProperties,
    FeatureKeys.ClientFiles,
    FeatureKeys.ClientAgreements,
    FeatureKeys.CustomForms,
    FeatureKeys.ClientImport,
    FeatureKeys.SupportChat,
    FeatureKeys.BookABuddy,
    FeatureKeys.SmartClientGroups,
    FeatureKeys.MemberMovement,
    FeatureKeys.FamilyAccounts,
  ],
  billingPayments: [
    FeatureKeys.Invoicing,
    FeatureKeys.BillingPayments,
    FeatureKeys.PaymentNotifications,
    FeatureKeys.FailedPaymentRecovery,
    FeatureKeys.PaymentMethods,
    FeatureKeys.Refunds,
    FeatureKeys.AccountCredits,
    FeatureKeys.Disputes,
    FeatureKeys.SitePayouts,
    FeatureKeys.PaymentGateways,
    FeatureKeys.TaxConfiguration,
    FeatureKeys.Proration,
    FeatureKeys.CorporatePayouts,
    FeatureKeys.RoyaltyDistribution,
  ],
  retailPointOfSale: [
    FeatureKeys.RetailPos,
    FeatureKeys.TerminalReaders,
    FeatureKeys.PromoCodes,
    FeatureKeys.GiftCards,
  ],
  reportingAnalytics: [
    FeatureKeys.StandardReports,
    FeatureKeys.BiDashboards,
    FeatureKeys.ScheduledReports,
    FeatureKeys.BigqueryReports,
    FeatureKeys.KpiPerformanceReport,
    FeatureKeys.RetentionDashboards,
    FeatureKeys.RetentionAtRisk,
    FeatureKeys.RetentionChurnAnalysis,
    FeatureKeys.RetentionEngagement,
    FeatureKeys.RetentionWinback,
    FeatureKeys.RetentionAttendanceTrends,
  ],
  staffManagement: [
    FeatureKeys.StaffManagement,
    FeatureKeys.AccessPolicies,
    FeatureKeys.StaffSmartGroups,
    FeatureKeys.Payroll,
    FeatureKeys.Commissions,
  ],
  marketingCommunications: [
    FeatureKeys.CommunicationTemplates,
    FeatureKeys.SmsMessaging,
    FeatureKeys.PushNotifications,
    FeatureKeys.LeadForms,
    FeatureKeys.SmartContactLists,
    FeatureKeys.SocialMediaSettings,
    FeatureKeys.CampaignTemplates,
    FeatureKeys.Whatsapp,
    FeatureKeys.BrandWideTemplates,
  ],
  contentEngagement: [
    FeatureKeys.ContentFaqs,
    FeatureKeys.ContentSuite,
    FeatureKeys.ExperiencePerformance,
    FeatureKeys.ExperienceChallenges,
    FeatureKeys.ExperienceLeaderboards,
    FeatureKeys.ExperienceStreaks,
    FeatureKeys.ExperienceBadges,
    FeatureKeys.ExperienceMilestones,
  ],
  memberApp: [
    FeatureKeys.MemberApp,
    FeatureKeys.MobileBooking,
    FeatureKeys.MobileCheckin,
    FeatureKeys.ContentDelivery,
    FeatureKeys.CustomBrandedApp,
    FeatureKeys.MemberAppPlanYourVisit,
  ],
  integrationsApis: [
    FeatureKeys.ClasspassIntegration,
    FeatureKeys.ZapierIntegration,
    FeatureKeys.ZoomIntegration,
    FeatureKeys.WellhubIntegration,
    FeatureKeys.InceptionIntegration,
    FeatureKeys.TopazIntegration,
    FeatureKeys.ReadApi,
    FeatureKeys.ApiWebhooksRead,
    FeatureKeys.ApiSandbox,
    FeatureKeys.ApiPremiumActions,
    FeatureKeys.FacebookPixel,
    FeatureKeys.GoogleTagManager,
    FeatureKeys.GrowHqManagement,
    FeatureKeys.AccurofitIntegration,
    FeatureKeys.SpiviIntegration,
    FeatureKeys.Marketplace,
    FeatureKeys.HubspotIntegration,
    FeatureKeys.EgymIntegration,
    FeatureKeys.FullApiReadWrite,
    FeatureKeys.WebhookEventsReadWrite,
    FeatureKeys.ApiSelfServiceKeys,
    FeatureKeys.BrandApiKeys,
    FeatureKeys.LocationApiKeys,
  ],
  aiAutomation: [
    FeatureKeys.HapanaAssistant,
    FeatureKeys.AiActions,
    FeatureKeys.AiDataInsights,
    FeatureKeys.AiPredictive,
    FeatureKeys.AiRecommendations,
    FeatureKeys.AiCharts,
    FeatureKeys.AiMetering,
    FeatureKeys.AiNaturalLanguageAnalytics,
  ],
  infrastructureSecurityCompliance: [
    FeatureKeys.SharedInfrastructure,
    FeatureKeys.Rbac,
    FeatureKeys.IsolatedDatabase,
    FeatureKeys.FullAuditLogs,
  ],
  professionalServices: [FeatureKeys.ProfessionalServices],
  accessControl: [
    FeatureKeys.AccessMacDoor,
    FeatureKeys.AccessScheduling,
    FeatureKeys.AccessEventLogging,
    FeatureKeys.AccessReporting,
    FeatureKeys.AccessSiteDoorManagement,
    FeatureKeys.AccessDigitalExperience,
    FeatureKeys.AccessController,
  ],
} as const satisfies Record<string, readonly FeatureKey[]>

export type FeatureCategory = keyof typeof FEATURE_CATEGORIES

export const FEATURE_CATEGORY_LABEL_KEYS: Record<FeatureCategory, string> = {
  locationBrandManagement: 'entitlements.categories.locationBrandManagement',
  membershipsPackages: 'entitlements.categories.membershipsPackages',
  schedulingClassManagement: 'entitlements.categories.schedulingClassManagement',
  bookingsAttendance: 'entitlements.categories.bookingsAttendance',
  clientManagement: 'entitlements.categories.clientManagement',
  billingPayments: 'entitlements.categories.billingPayments',
  retailPointOfSale: 'entitlements.categories.retailPointOfSale',
  reportingAnalytics: 'entitlements.categories.reportingAnalytics',
  staffManagement: 'entitlements.categories.staffManagement',
  marketingCommunications: 'entitlements.categories.marketingCommunications',
  contentEngagement: 'entitlements.categories.contentEngagement',
  memberApp: 'entitlements.categories.memberApp',
  integrationsApis: 'entitlements.categories.integrationsApis',
  aiAutomation: 'entitlements.categories.aiAutomation',
  infrastructureSecurityCompliance: 'entitlements.categories.infrastructureSecurityCompliance',
  professionalServices: 'entitlements.categories.professionalServices',
  accessControl: 'entitlements.categories.accessControl',
}

/** A category and the features a given plan includes within it. */
export interface TierFeatureGroup {
  category: FeatureCategory
  features: FeatureKey[]
}

/**
 * The features a plan tier includes, grouped by category and with empty categories dropped. Pure
 * and derived from PLAN_FEATURES + FEATURE_CATEGORIES — the same maps the entitlement engine gates
 * on — so a "what's included" view built from this cannot drift from what the tier actually grants.
 * Shared so the frontend and backend group features the same way rather than each re-deriving it.
 */
export function getTierFeatureGroups(tier: PlanTier): TierFeatureGroup[] {
  const included = PLAN_FEATURES[tier]
  return (Object.keys(FEATURE_CATEGORIES) as FeatureCategory[])
    .map((category) => ({
      category,
      features: FEATURE_CATEGORIES[category].filter((feature) => included.has(feature)),
    }))
    .filter((group) => group.features.length > 0)
}

// ---------------------------------------------------------------------------
// Pricing (numeric only — display text lives in i18n translations)
// ---------------------------------------------------------------------------

/** The billing cadences tiering prices in. One today; a fixed set either way, not open text. */
export const PRICING_INTERVALS = ['fortnightly'] as const

export type PricingInterval = (typeof PRICING_INTERVALS)[number]

export interface EntitlementPricing {
  price: number
  // The repo already closed this set — reuse it rather than reopening it as a bare string, so a
  // typo'd code cannot type-check its way through to the UI when pricing goes multi-currency.
  currency: CurrencyValue
  interval: PricingInterval
}

export const PLAN_PRICING: Record<PlanTier, EntitlementPricing> = {
  basic: { price: 199, currency: Currency.AUD, interval: 'fortnightly' },
  premium: { price: 299, currency: Currency.AUD, interval: 'fortnightly' },
  pro: { price: 399, currency: Currency.AUD, interval: 'fortnightly' },
}

export const THEME_PRICING: Record<ThemeKey, EntitlementPricing> = {
  access: { price: 39, currency: Currency.AUD, interval: 'fortnightly' },
  experience: { price: 39, currency: Currency.AUD, interval: 'fortnightly' },
  retention: { price: 39, currency: Currency.AUD, interval: 'fortnightly' },
  hapana_ai: { price: 39, currency: Currency.AUD, interval: 'fortnightly' },
}
